import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Calendar, Video, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import logo from "@/assets/logo.png";
import RegistrationForm from "@/components/RegistrationForm";
import PageSEO from "@/components/PageSEO";

const ThankYou = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const zoomLink = "https://us06web.zoom.us/j/89059944627?pwd=bEDJtgq8yvZ13Czsoltd7DWqUKaXFZ.1";

  return (
    <div className="min-h-screen bg-[hsl(220,20%,12%)] relative overflow-hidden">
      <PageSEO
        title="You're Registered | Marquetta Bryant"
        description="Registration confirmed. Check your email for event details."
        canonical="https://marquettabryant.com/thank-you"
        noindex
      />
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fade-in"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  backgroundColor: ['#d8239b', '#f5c518', '#1e90ff', '#ff69b4', '#0364c4'][Math.floor(Math.random() * 5)],
                  transform: `rotate(${Math.random() * 360}deg)`,
                  animation: `fall ${2 + Math.random() * 2}s linear forwards`,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Minimal Header */}
      <header className="py-6 border-b border-white/10">
        <div className="container mx-auto px-4">
          <Link to="/">
            <img src={logo} alt="Marquetta" className="h-10 mx-auto" />
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-2xl mx-auto text-center animate-fade-in">
          {/* Success Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#f5c518] to-[#d8239b] flex items-center justify-center shadow-2xl">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            Payment Confirmed.
          </h1>

          {!registered ? (
            <>
              <p className="text-white/80 leading-relaxed mb-8 max-w-xl mx-auto text-lg">
                Before you receive your access link, please complete a short registration so we can route you to the correct experience.
              </p>
              <p className="text-[#f5c518] font-medium mb-10">
                This takes less than one minute.
              </p>

              {/* Registration Form */}
              <RegistrationForm
                attendeeType="supporter"
                onRegistrationComplete={() => setRegistered(true)}
              />
            </>
          ) : (
            <>
              <p className="text-xl md:text-2xl text-[#f5c518] font-medium mb-6">
                Registration Complete! You're in.
              </p>

              {/* Access Info */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-white/10 mb-10 text-left space-y-6">
                <h2 className="text-2xl font-bold text-white text-center">Your Access</h2>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1e90ff]/20 to-[#0364c4]/20 flex items-center justify-center flex-shrink-0 border border-[#1e90ff]/30">
                    <Video className="w-5 h-5 text-[#1e90ff]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Zoom Link</h3>
                    <a 
                      href={zoomLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#1e90ff] hover:text-[#1e90ff]/80 transition-colors flex items-center gap-1"
                    >
                      Join the Live Experience
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f5c518]/20 to-[#d8239b]/20 flex items-center justify-center flex-shrink-0 border border-[#f5c518]/30">
                    <Calendar className="w-5 h-5 text-[#f5c518]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Schedule</h3>
                    <p className="text-white/70">Feb 10–12, 2026 @ 11:00 AM EST</p>
                    <p className="text-white/50 text-sm mt-1">This access is for live attendance only.</p>
                  </div>
                </div>
              </div>

              <p className="text-white/60 mb-8 text-sm">
                A confirmation email with your access details has been sent to your inbox.
              </p>

              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-[#f5c518] hover:bg-[#f5c518]/90 text-[#1a1a2e] font-bold px-10 py-6 text-lg group shadow-lg"
                >
                  Return to Homepage
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="py-6 border-t border-white/10 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Marquetta Bryant. All Rights Reserved.
          </p>
        </div>
      </footer>

      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default ThankYou;
